import type { PayloadAction } from '@reduxjs/toolkit';
import { createSelector, createSlice } from '@reduxjs/toolkit';

import type { RequestPaymentParams } from '@/store/server/common/payment/payment.query';

// Required
type PaymentState = Required<RequestPaymentParams>;

// export type TProductListItem = NonNullable<RequestPaymentParams['productList']>[number];
export type TPayment = RequestPaymentParams['payment'];
export type TDelivery = NonNullable<RequestPaymentParams['delivery']>;
export type TCashReceipt = NonNullable<RequestPaymentParams['cashReceipt']>;
// 현금영수증 종류 none - 미발행, deduction - 소득공제, proof - 지출증빙
export type TCashReceiptType = 'none' | 'deduction' | 'proof';

// 초기 상품 목록
// const initialProductList: PaymentState['productList'] = [];
// const initialCoupon: PaymentState['couponList'] = [];

// 초기 결제 정보
const initialPayment = {
  redirectUrl: '',
  method: 'M' as const,
};

// 초기 배송지 정보
const initialDelivery: TDelivery = {
  deliveryName: '',
  deliveryPhone: '',
  postNumber: '',
  address: '',
  addressDetail: '',
  deliveryRequest: '',
};

// 초기 현금 영수증 정보
const initialCashReceipt: TCashReceipt = {
  receiptType: 'none',
  receiptInfoType: 'phone',
  receiptInfo: '',
};

// 초기 상태
const initialState: PaymentState = {
  type: 'normal', // 기본 주문 유형
  orderId: '',
  // productList: initialProductList,
  // couponList: initialCoupon,
  payment: initialPayment,
  delivery: initialDelivery,
  cashReceipt: initialCashReceipt,
};

const paymentSlice = createSlice({
  name: 'payment',
  initialState,
  reducers: {
    // 상품
    // 상품 목록 설정
    // setProductList: (state, action: PayloadAction<PaymentState['productList']>) => {
    //   state.productList = action.payload;
    // },
    // // 상품 목록 추가
    // addProductListItem: (state, action: PayloadAction<TProductListItem>) => {
    //   state.productList.push(action.payload);
    // },
    // // 상품 목록 삭제
    // removeProductListItem: (state, action: PayloadAction<{ productId: string }>) => {
    //   state.productList = state.productList.filter((product) => product.productId !== action.payload.productId);
    // },

    // 결제
    // 결제 설정
    setPayment: (state, action: PayloadAction<TPayment>) => {
      state.payment = action.payload;
    },
    // redirectUrl 설정
    setRedirectUrl: (state, action: PayloadAction<PaymentState['payment']['redirectUrl']>) => {
      state.payment.redirectUrl = action.payload;
    },
    // 결제 방법 설정
    setPaymentMethod: (state, action: PayloadAction<PaymentState['payment']['method']>) => {
      state.payment.method = action.payload;

      // 결제 방법이 변경되면 현금영수증 정보 초기화
      state.cashReceipt = initialCashReceipt;
    },

    // 배송
    // 배송 설정
    setDelivery: (state, action: PayloadAction<TDelivery>) => {
      state.delivery = action.payload;
    },
    // 수령인 설정
    setDeliveryName: (state, action: PayloadAction<PaymentState['delivery']['deliveryName']>) => {
      state.delivery.deliveryName = action.payload;
    },
    // 수령인 전화번호 설정
    setDeliveryPhone: (state, action: PayloadAction<PaymentState['delivery']['deliveryPhone']>) => {
      state.delivery.deliveryPhone = action.payload;
    },
    // 우편번호 설정
    setPostNumber: (state, action: PayloadAction<PaymentState['delivery']['postNumber']>) => {
      state.delivery.postNumber = action.payload;
    },
    // 주소 설정
    setAddress: (state, action: PayloadAction<PaymentState['delivery']['address']>) => {
      state.delivery.address = action.payload;
    },
    // 상세주소 설정
    setAddressDetail: (state, action: PayloadAction<PaymentState['delivery']['addressDetail']>) => {
      state.delivery.addressDetail = action.payload;
    },
    // 배송 요청사항 설정
    setDeliveryRequest: (state, action: PayloadAction<PaymentState['delivery']['deliveryRequest']>) => {
      state.delivery.deliveryRequest = action.payload;
    },

    // 현금 영수증
    // 현금 영수증 설정
    setCashReceipt: (state, action: PayloadAction<TCashReceipt>) => {
      state.cashReceipt = action.payload;
    },
    // 현금 영수증 종류 설정
    setCashReceiptType: (state, action: PayloadAction<TCashReceiptType>) => {
      state.cashReceipt.receiptType = action.payload;

      // 현금 영수증 종류가 변경되면 현금 영수증 정보 타입 및 정보 초기화
      state.cashReceipt.receiptInfoType = initialCashReceipt.receiptInfoType;
      state.cashReceipt.receiptInfo = initialCashReceipt.receiptInfo;
    },
    // 현금 영수증 정보 타입 설정
    setCashReceiptInfoType: (state, action: PayloadAction<PaymentState['cashReceipt']['receiptInfoType']>) => {
      state.cashReceipt.receiptInfoType = action.payload;

      // 현금 영수증 정보 타입이 변경되면 현금 영수증 정보 초기화
      state.cashReceipt.receiptInfo = initialCashReceipt.receiptInfo;
    },
    // 현금 영수증 정보 설정
    setCashReceiptInfo: (state, action: PayloadAction<PaymentState['cashReceipt']['receiptInfo']>) => {
      state.cashReceipt.receiptInfo = action.payload;
    },
  },
});

const paymentSelector = (state: { payment: PaymentState }) => state.payment;

// 배송지 정보가 모두 입력되었는지 확인
const hasDeliverySelector = () =>
  createSelector([paymentSelector], ({ delivery }) => {
    return (
      !!delivery.deliveryName &&
      !!delivery.deliveryPhone &&
      !!delivery.postNumber &&
      !!delivery.address &&
      !!delivery.addressDetail
    );
  });

export const paymentActions = paymentSlice.actions;

export const paymentSelectors = {
  hasDeliverySelector,
};

export default paymentSlice;
